/**
 * Deterministic hand-authored reference step cycle for imitation feedforward.
 *
 * Pure data + pure functions — no physics, no controller state.
 * Canonical cycle is a 0.22 m forward step (knee-led swing, heel-biased land).
 * Phase timeline: prepare → lift → swing → land → load → settle.
 *
 * Joint convention matches the muscle controller (STAND-relative offsets):
 *   +sag = extend / plantarflex; −sag = flex / dorsiflex
 *   +lat = lean/abduct toward world +X (right)
 * Role channels (swing/stance) are mapped to L/R by the caller.
 */

/** Nominal reference step length (m) this keyframe table was authored for. */
export const REFERENCE_STEP_LENGTH_M = 0.22 as const;

/** Phase group labels used for blend weights and keyframe tags. */
export type ReferencePhaseGroup =
  | 'prepare'
  | 'lift'
  | 'swing'
  | 'land'
  | 'load'
  | 'settle';

/**
 * Role-relative joint targets (rad, STAND-relative).
 * All fields finite; sample/clamp helpers enforce joint limits.
 */
export interface ReferenceJointTargets {
  readonly swingHipSag: number;
  readonly swingHipLat: number;
  readonly swingKnee: number;
  readonly swingAnkleSag: number;
  readonly swingAnkleLat: number;
  readonly stanceHipSag: number;
  readonly stanceHipLat: number;
  readonly stanceKnee: number;
  readonly stanceAnkleSag: number;
  readonly stanceAnkleLat: number;
  readonly spineSag: number;
  readonly spineLat: number;
  /** Optional contralateral arm swing (shoulder sagittal). */
  readonly swingShoulderSag: number;
  readonly stanceShoulderSag: number;
}

/** One keyframe on the normalized phase timeline u ∈ [0, 1]. */
export interface ReferenceStepKeyframe {
  /** Normalized phase in [0, 1]. Must be sorted non-decreasing. */
  readonly u: number;
  readonly group: ReferencePhaseGroup;
  readonly targets: ReferenceJointTargets;
}

/**
 * Per-phase-group feedforward blend weights in [0, 1].
 * `final = (1−w)·feedback + w·reference`. All-zero = identity (no blend).
 */
export interface ReferenceStepBlendWeights {
  readonly prepare: number;
  readonly lift: number;
  readonly swing: number;
  readonly land: number;
  readonly load: number;
  readonly settle: number;
}

/** Frozen zero blend — byte-identical to omitting feedforward. */
export const ZERO_REFERENCE_STEP_BLEND: Readonly<ReferenceStepBlendWeights> =
  Object.freeze({
    prepare: 0,
    lift: 0,
    swing: 0,
    land: 0,
    load: 0,
    settle: 0,
  });

/** Optional shape scalars (search knobs). Defaults leave the authored table as-is. */
export interface ReferenceStepShapeScalars {
  /** Scale swing hip/knee flexion amplitude (1 = authored). */
  readonly swingFlexScale?: number;
  /** Scale stance brace amplitude (1 = authored). */
  readonly stanceBraceScale?: number;
  /** Scale spine pitch amplitude (1 = authored). */
  readonly spineScale?: number;
  /** Scale arm swing amplitude (1 = authored). */
  readonly armSwingScale?: number;
}

export const DEFAULT_REFERENCE_STEP_SHAPE: Readonly<
  Required<ReferenceStepShapeScalars>
> = Object.freeze({
  swingFlexScale: 1,
  stanceBraceScale: 1,
  spineScale: 1,
  armSwingScale: 1,
});

// ---------------------------------------------------------------------------
// Anatomical joint clamps (muscleBalance / anatomical preset, conservative)
// ---------------------------------------------------------------------------

/** Conservative STAND-relative clamp bands (rad) used by sample + tests. */
export const REFERENCE_JOINT_LIMITS = Object.freeze({
  hipSag: { min: -1.0, max: 0.55 } as const,
  hipLat: { min: -0.55, max: 0.55 } as const,
  knee: { min: -1.4, max: 0.12 } as const,
  ankleSag: { min: -0.45, max: 0.45 } as const,
  ankleLat: { min: -0.35, max: 0.35 } as const,
  spineSag: { min: -0.4, max: 0.35 } as const,
  spineLat: { min: -0.4, max: 0.4 } as const,
  shoulderSag: { min: -1.3, max: 0.3 } as const,
});

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

function clamp01(v: number): number {
  return clamp(v, 0, 1);
}

/** Hermite smoothstep on [0,1]: 3t²−2t³ (C1). */
export function smoothstep01(t: number): number {
  const u = clamp01(t);
  return u * u * (3 - 2 * u);
}

export function clampReferenceJointTargets(
  t: Readonly<ReferenceJointTargets>,
): ReferenceJointTargets {
  const L = REFERENCE_JOINT_LIMITS;
  return {
    swingHipSag: clamp(t.swingHipSag, L.hipSag.min, L.hipSag.max),
    swingHipLat: clamp(t.swingHipLat, L.hipLat.min, L.hipLat.max),
    swingKnee: clamp(t.swingKnee, L.knee.min, L.knee.max),
    swingAnkleSag: clamp(t.swingAnkleSag, L.ankleSag.min, L.ankleSag.max),
    swingAnkleLat: clamp(t.swingAnkleLat, L.ankleLat.min, L.ankleLat.max),
    stanceHipSag: clamp(t.stanceHipSag, L.hipSag.min, L.hipSag.max),
    stanceHipLat: clamp(t.stanceHipLat, L.hipLat.min, L.hipLat.max),
    stanceKnee: clamp(t.stanceKnee, L.knee.min, L.knee.max),
    stanceAnkleSag: clamp(t.stanceAnkleSag, L.ankleSag.min, L.ankleSag.max),
    stanceAnkleLat: clamp(t.stanceAnkleLat, L.ankleLat.min, L.ankleLat.max),
    spineSag: clamp(t.spineSag, L.spineSag.min, L.spineSag.max),
    spineLat: clamp(t.spineLat, L.spineLat.min, L.spineLat.max),
    swingShoulderSag: clamp(
      t.swingShoulderSag,
      L.shoulderSag.min,
      L.shoulderSag.max,
    ),
    stanceShoulderSag: clamp(
      t.stanceShoulderSag,
      L.shoulderSag.min,
      L.shoulderSag.max,
    ),
  };
}

function lerpTargets(
  a: Readonly<ReferenceJointTargets>,
  b: Readonly<ReferenceJointTargets>,
  s: number,
): ReferenceJointTargets {
  const mix = (x: number, y: number) => x + (y - x) * s;
  return {
    swingHipSag: mix(a.swingHipSag, b.swingHipSag),
    swingHipLat: mix(a.swingHipLat, b.swingHipLat),
    swingKnee: mix(a.swingKnee, b.swingKnee),
    swingAnkleSag: mix(a.swingAnkleSag, b.swingAnkleSag),
    swingAnkleLat: mix(a.swingAnkleLat, b.swingAnkleLat),
    stanceHipSag: mix(a.stanceHipSag, b.stanceHipSag),
    stanceHipLat: mix(a.stanceHipLat, b.stanceHipLat),
    stanceKnee: mix(a.stanceKnee, b.stanceKnee),
    stanceAnkleSag: mix(a.stanceAnkleSag, b.stanceAnkleSag),
    stanceAnkleLat: mix(a.stanceAnkleLat, b.stanceAnkleLat),
    spineSag: mix(a.spineSag, b.spineSag),
    spineLat: mix(a.spineLat, b.spineLat),
    swingShoulderSag: mix(a.swingShoulderSag, b.swingShoulderSag),
    stanceShoulderSag: mix(a.stanceShoulderSag, b.stanceShoulderSag),
  };
}

function scaleTargets(
  t: Readonly<ReferenceJointTargets>,
  shape: Readonly<Required<ReferenceStepShapeScalars>>,
): ReferenceJointTargets {
  const sf = shape.swingFlexScale;
  const sb = shape.stanceBraceScale;
  const sp = shape.spineScale;
  const sa = shape.armSwingScale;
  return {
    swingHipSag: t.swingHipSag * sf,
    swingHipLat: t.swingHipLat * sf,
    swingKnee: t.swingKnee * sf,
    swingAnkleSag: t.swingAnkleSag * sf,
    swingAnkleLat: t.swingAnkleLat * sf,
    stanceHipSag: t.stanceHipSag * sb,
    stanceHipLat: t.stanceHipLat * sb,
    stanceKnee: t.stanceKnee * sb,
    stanceAnkleSag: t.stanceAnkleSag * sb,
    stanceAnkleLat: t.stanceAnkleLat * sb,
    spineSag: t.spineSag * sp,
    spineLat: t.spineLat * sp,
    swingShoulderSag: t.swingShoulderSag * sa,
    stanceShoulderSag: t.stanceShoulderSag * sa,
  };
}

function standTargets(): ReferenceJointTargets {
  return {
    swingHipSag: 0,
    swingHipLat: 0,
    swingKnee: 0,
    swingAnkleSag: 0,
    swingAnkleLat: 0,
    stanceHipSag: 0,
    stanceHipLat: 0,
    stanceKnee: 0,
    stanceAnkleSag: 0,
    stanceAnkleLat: 0,
    spineSag: 0,
    spineLat: 0,
    swingShoulderSag: 0,
    stanceShoulderSag: 0,
  };
}

/**
 * Hand-authored 16-keyframe 0.22 m right-leg-forward-style cycle.
 * Knee-led swing, heel-biased land (dorsiflex before plant), mild arm counter-swing.
 * Stance hip lat sign assumes stance on the left (swing right); callers with
 * opposite swing flip lateral signs via {@link mirrorReferenceLaterals}.
 */
export const REFERENCE_STEP_KEYFRAMES: readonly ReferenceStepKeyframe[] =
  Object.freeze([
    {
      u: 0,
      group: 'prepare' as const,
      targets: standTargets(),
    },
    {
      // Early prepare: load transfer toward stance, mild stance brace.
      u: 0.06,
      group: 'prepare' as const,
      targets: {
        swingHipSag: 0.02,
        swingHipLat: 0.04,
        swingKnee: 0.04,
        swingAnkleSag: 0.02,
        swingAnkleLat: 0,
        stanceHipSag: 0.1,
        stanceHipLat: -0.12,
        stanceKnee: 0.1,
        stanceAnkleSag: 0.06,
        stanceAnkleLat: 0.04,
        spineSag: 0.04,
        spineLat: 0.06,
        swingShoulderSag: -0.05,
        stanceShoulderSag: 0.05,
      },
    },
    {
      // Prepare ready: COM over stance, swing unloaded.
      u: 0.12,
      group: 'prepare' as const,
      targets: {
        swingHipSag: 0.04,
        swingHipLat: 0.06,
        swingKnee: 0.06,
        swingAnkleSag: 0.02,
        swingAnkleLat: 0,
        stanceHipSag: 0.14,
        stanceHipLat: -0.16,
        stanceKnee: 0.12,
        stanceAnkleSag: 0.08,
        stanceAnkleLat: 0.05,
        spineSag: 0.06,
        spineLat: 0.08,
        swingShoulderSag: -0.08,
        stanceShoulderSag: 0.08,
      },
    },
    {
      // Lift onset: knee-led tuck (flex −), hip begins flexion.
      u: 0.18,
      group: 'lift' as const,
      targets: {
        swingHipSag: -0.12,
        swingHipLat: 0.05,
        swingKnee: -0.35,
        swingAnkleSag: 0.04,
        swingAnkleLat: 0,
        stanceHipSag: 0.12,
        stanceHipLat: -0.14,
        stanceKnee: 0.12,
        stanceAnkleSag: 0.06,
        stanceAnkleLat: 0.04,
        spineSag: 0.05,
        spineLat: 0.06,
        swingShoulderSag: -0.12,
        stanceShoulderSag: 0.12,
      },
    },
    {
      // Lift apex clearance: deep knee flex + hip flex.
      u: 0.26,
      group: 'lift' as const,
      targets: {
        swingHipSag: -0.28,
        swingHipLat: 0.04,
        swingKnee: -0.55,
        swingAnkleSag: 0.08,
        swingAnkleLat: 0,
        stanceHipSag: 0.1,
        stanceHipLat: -0.12,
        stanceKnee: 0.12,
        stanceAnkleSag: 0.05,
        stanceAnkleLat: 0.03,
        spineSag: 0.04,
        spineLat: 0.05,
        swingShoulderSag: -0.18,
        stanceShoulderSag: 0.18,
      },
    },
    {
      // Mid-swing: knee still leading, hip flex for 0.22 m reach.
      u: 0.34,
      group: 'swing' as const,
      targets: {
        swingHipSag: -0.42,
        swingHipLat: 0.03,
        swingKnee: -0.48,
        swingAnkleSag: 0.05,
        swingAnkleLat: 0,
        stanceHipSag: 0.08,
        stanceHipLat: -0.1,
        stanceKnee: 0.1,
        stanceAnkleSag: 0.04,
        stanceAnkleLat: 0.02,
        spineSag: 0.03,
        spineLat: 0.04,
        swingShoulderSag: -0.22,
        stanceShoulderSag: 0.22,
      },
    },
    {
      // Late swing: knee extends, hip holds reach.
      u: 0.42,
      group: 'swing' as const,
      targets: {
        swingHipSag: -0.48,
        swingHipLat: 0.02,
        swingKnee: -0.28,
        swingAnkleSag: -0.02,
        swingAnkleLat: 0,
        stanceHipSag: 0.06,
        stanceHipLat: -0.08,
        stanceKnee: 0.1,
        stanceAnkleSag: 0.03,
        stanceAnkleLat: 0.02,
        spineSag: 0.02,
        spineLat: 0.03,
        swingShoulderSag: -0.2,
        stanceShoulderSag: 0.2,
      },
    },
    {
      // Pre-land: heel bias (dorsiflex −), knee almost straight.
      u: 0.5,
      group: 'land' as const,
      targets: {
        swingHipSag: -0.38,
        swingHipLat: 0.01,
        swingKnee: -0.12,
        swingAnkleSag: -0.1,
        swingAnkleLat: 0,
        stanceHipSag: 0.05,
        stanceHipLat: -0.06,
        stanceKnee: 0.1,
        stanceAnkleSag: 0.02,
        stanceAnkleLat: 0.01,
        spineSag: 0.02,
        spineLat: 0.02,
        swingShoulderSag: -0.14,
        stanceShoulderSag: 0.14,
      },
    },
    {
      // Touchdown: heel first, soft knee, stance still braced.
      u: 0.58,
      group: 'land' as const,
      targets: {
        swingHipSag: -0.28,
        swingHipLat: 0,
        swingKnee: -0.08,
        swingAnkleSag: -0.06,
        swingAnkleLat: 0,
        stanceHipSag: 0.04,
        stanceHipLat: -0.04,
        stanceKnee: 0.08,
        stanceAnkleSag: 0.02,
        stanceAnkleLat: 0,
        spineSag: 0.01,
        spineLat: 0.01,
        swingShoulderSag: -0.08,
        stanceShoulderSag: 0.08,
      },
    },
    {
      // Early load: new (front) leg accepts, stance (rear) extends.
      u: 0.66,
      group: 'load' as const,
      targets: {
        swingHipSag: -0.12,
        swingHipLat: 0.02,
        swingKnee: 0.06,
        swingAnkleSag: 0.04,
        swingAnkleLat: 0.02,
        stanceHipSag: 0.18,
        stanceHipLat: -0.04,
        stanceKnee: 0.04,
        stanceAnkleSag: 0.06,
        stanceAnkleLat: 0,
        spineSag: -0.04,
        spineLat: 0,
        swingShoulderSag: -0.04,
        stanceShoulderSag: 0.04,
      },
    },
    {
      // Mid load: weight over mid-support, both legs plant tone.
      u: 0.74,
      group: 'load' as const,
      targets: {
        swingHipSag: -0.04,
        swingHipLat: 0.02,
        swingKnee: 0.08,
        swingAnkleSag: 0.02,
        swingAnkleLat: 0.01,
        stanceHipSag: 0.1,
        stanceHipLat: -0.02,
        stanceKnee: 0.06,
        stanceAnkleSag: 0.02,
        stanceAnkleLat: 0,
        spineSag: -0.02,
        spineLat: 0,
        swingShoulderSag: -0.02,
        stanceShoulderSag: 0.02,
      },
    },
    {
      // Late load: settle toward stand, residual plant.
      u: 0.84,
      group: 'load' as const,
      targets: {
        swingHipSag: 0.02,
        swingHipLat: 0.01,
        swingKnee: 0.04,
        swingAnkleSag: 0,
        swingAnkleLat: 0,
        stanceHipSag: 0.04,
        stanceHipLat: -0.01,
        stanceKnee: 0.04,
        stanceAnkleSag: 0,
        stanceAnkleLat: 0,
        spineSag: 0,
        spineLat: 0,
        swingShoulderSag: 0,
        stanceShoulderSag: 0,
      },
    },
    {
      // Settle: near-stand dual support.
      u: 0.92,
      group: 'settle' as const,
      targets: {
        swingHipSag: 0.01,
        swingHipLat: 0,
        swingKnee: 0.02,
        swingAnkleSag: -0.02,
        swingAnkleLat: 0,
        stanceHipSag: 0.01,
        stanceHipLat: 0,
        stanceKnee: 0.02,
        stanceAnkleSag: -0.02,
        stanceAnkleLat: 0,
        spineSag: 0,
        spineLat: 0,
        swingShoulderSag: 0,
        stanceShoulderSag: 0,
      },
    },
    {
      // Cyclic end = stand (must match u=0 targets).
      u: 1,
      group: 'settle' as const,
      targets: standTargets(),
    },
  ]);

/** Resolve optional shape scalars with production defaults. */
export function resolveReferenceStepShape(
  shape?: Readonly<ReferenceStepShapeScalars> | null,
): Required<ReferenceStepShapeScalars> {
  return {
    swingFlexScale:
      typeof shape?.swingFlexScale === 'number' && Number.isFinite(shape.swingFlexScale)
        ? shape.swingFlexScale
        : DEFAULT_REFERENCE_STEP_SHAPE.swingFlexScale,
    stanceBraceScale:
      typeof shape?.stanceBraceScale === 'number' &&
      Number.isFinite(shape.stanceBraceScale)
        ? shape.stanceBraceScale
        : DEFAULT_REFERENCE_STEP_SHAPE.stanceBraceScale,
    spineScale:
      typeof shape?.spineScale === 'number' && Number.isFinite(shape.spineScale)
        ? shape.spineScale
        : DEFAULT_REFERENCE_STEP_SHAPE.spineScale,
    armSwingScale:
      typeof shape?.armSwingScale === 'number' && Number.isFinite(shape.armSwingScale)
        ? shape.armSwingScale
        : DEFAULT_REFERENCE_STEP_SHAPE.armSwingScale,
  };
}

/**
 * Piecewise smoothstep interpolation of the keyframe table at normalized phase u.
 * Always returns finite, limit-clamped targets. Cyclic: u=0 and u=1 are stand.
 */
export function sampleReferenceStep(
  phase01: number,
  opts?: {
    readonly shape?: Readonly<ReferenceStepShapeScalars> | null;
    /** Scale amplitudes by commanded/authored length ratio (default 1). */
    readonly stepLengthM?: number;
  },
): ReferenceJointTargets {
  const u = clamp01(phase01);
  const frames = REFERENCE_STEP_KEYFRAMES;
  let i1 = 0;
  while (i1 < frames.length - 1 && frames[i1 + 1]!.u < u - 1e-15) i1 += 1;
  const i0 = i1;
  const iNext = Math.min(i1 + 1, frames.length - 1);
  const a = frames[i0]!;
  const b = frames[iNext]!;
  const span = b.u - a.u;
  const s = span > 1e-12 ? smoothstep01((u - a.u) / span) : 0;
  let mixed = lerpTargets(a.targets, b.targets, s);

  const lengthScale =
    typeof opts?.stepLengthM === 'number' &&
    Number.isFinite(opts.stepLengthM) &&
    opts.stepLengthM > 0
      ? clamp(opts.stepLengthM / REFERENCE_STEP_LENGTH_M, 0.25, 1.5)
      : 1;
  if (lengthScale !== 1) {
    mixed = {
      ...mixed,
      swingHipSag: mixed.swingHipSag * lengthScale,
      swingKnee: mixed.swingKnee * lengthScale,
      stanceHipSag: mixed.stanceHipSag * lengthScale,
    };
  }

  const shape = resolveReferenceStepShape(opts?.shape);
  return clampReferenceJointTargets(scaleTargets(mixed, shape));
}

/**
 * Flip lateral (and optional shoulder) signs for left-swing / right-stance.
 * Authored table assumes swing=right, stance=left laterals.
 */
export function mirrorReferenceLaterals(
  t: Readonly<ReferenceJointTargets>,
): ReferenceJointTargets {
  return {
    ...t,
    swingHipLat: -t.swingHipLat,
    swingAnkleLat: -t.swingAnkleLat,
    stanceHipLat: -t.stanceHipLat,
    stanceAnkleLat: -t.stanceAnkleLat,
    spineLat: -t.spineLat,
    // Swap arm roles with the legs.
    swingShoulderSag: t.stanceShoulderSag,
    stanceShoulderSag: t.swingShoulderSag,
  };
}

/**
 * Map controller step phase + elapsed time to normalized cycle phase u ∈ [0, 1].
 * Durations match the certified P1A single-support schedule (nominal).
 */
export function referencePhaseFromControllerState(args: {
  readonly stepPhase:
    | 'none'
    | 'prepare'
    | 'lift'
    | 'hold'
    | 'place'
    | 'load';
  readonly stepPhaseElapsedS: number;
  readonly prepareMinS?: number;
  readonly liftRampS?: number;
  readonly holdS?: number;
  readonly replaceS?: number;
  readonly loadS?: number;
  readonly settleS?: number;
}): number {
  const prepareS = args.prepareMinS ?? 0.2;
  const liftS = args.liftRampS ?? 0.12;
  const holdS = args.holdS ?? 0.25;
  const placeS = args.replaceS ?? 0.4;
  const loadS = args.loadS ?? 0.5;
  const settleS = args.settleS ?? 0.3;
  const total = prepareS + liftS + holdS + placeS + loadS + settleS;
  if (total <= 1e-9) return 0;

  const e = Math.max(0, args.stepPhaseElapsedS);
  let base = 0;
  let dur = 0;
  switch (args.stepPhase) {
    case 'prepare':
      base = 0;
      dur = prepareS;
      break;
    case 'lift':
      base = prepareS;
      dur = liftS;
      break;
    case 'hold':
      base = prepareS + liftS;
      dur = holdS;
      break;
    case 'place':
      base = prepareS + liftS + holdS;
      dur = placeS;
      break;
    case 'load':
      base = prepareS + liftS + holdS + placeS;
      dur = loadS;
      break;
    case 'none':
    default:
      // Settle / idle: park at end of settle window.
      return 1;
  }
  const local = clamp01(e / Math.max(dur, 1e-6));
  return clamp01((base + local * dur) / total);
}

/** Phase-group for blend weight lookup from controller step phase. */
export function referencePhaseGroupFromStepPhase(
  stepPhase: 'none' | 'prepare' | 'lift' | 'hold' | 'place' | 'load',
): ReferencePhaseGroup | null {
  switch (stepPhase) {
    case 'prepare':
      return 'prepare';
    case 'lift':
      return 'lift';
    case 'hold':
      return 'swing';
    case 'place':
      return 'land';
    case 'load':
      return 'load';
    case 'none':
      return 'settle';
    default:
      return null;
  }
}

export function resolveReferenceStepBlendWeights(
  w?: Readonly<Partial<ReferenceStepBlendWeights>> | null,
): ReferenceStepBlendWeights {
  if (!w) return ZERO_REFERENCE_STEP_BLEND;
  return {
    prepare: clamp01(finiteOr0(w.prepare)),
    lift: clamp01(finiteOr0(w.lift)),
    swing: clamp01(finiteOr0(w.swing)),
    land: clamp01(finiteOr0(w.land)),
    load: clamp01(finiteOr0(w.load)),
    settle: clamp01(finiteOr0(w.settle)),
  };
}

function finiteOr0(v: number | undefined): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

/** True when every blend weight is exactly 0 (identity path). */
export function isZeroReferenceBlend(
  w: Readonly<ReferenceStepBlendWeights>,
): boolean {
  return (
    w.prepare === 0 &&
    w.lift === 0 &&
    w.swing === 0 &&
    w.land === 0 &&
    w.load === 0 &&
    w.settle === 0
  );
}

export function blendWeightForPhaseGroup(
  w: Readonly<ReferenceStepBlendWeights>,
  group: ReferencePhaseGroup | null,
): number {
  if (!group) return 0;
  return w[group] ?? 0;
}

/**
 * Linear blend of feedback command toward reference target.
 * `out = (1−w)·feedback + w·reference`. w=0 returns feedback unchanged.
 */
export function blendScalarTowardReference(
  feedback: number,
  reference: number,
  weight01: number,
): number {
  const w = clamp01(weight01);
  if (w === 0) return feedback;
  if (w === 1) return reference;
  return feedback + w * (reference - feedback);
}

/**
 * Mean absolute joint error between two target snapshots (tracking cost).
 * Averages over the 14 scalar channels (12 core + 2 shoulders).
 */
export function meanJointTrackingErrorRad(
  a: Readonly<ReferenceJointTargets>,
  b: Readonly<ReferenceJointTargets>,
): number {
  const keys: (keyof ReferenceJointTargets)[] = [
    'swingHipSag',
    'swingHipLat',
    'swingKnee',
    'swingAnkleSag',
    'swingAnkleLat',
    'stanceHipSag',
    'stanceHipLat',
    'stanceKnee',
    'stanceAnkleSag',
    'stanceAnkleLat',
    'spineSag',
    'spineLat',
    'swingShoulderSag',
    'stanceShoulderSag',
  ];
  let sum = 0;
  for (const k of keys) sum += Math.abs(a[k] - b[k]);
  return sum / keys.length;
}

/**
 * COM forward-progress match cost (m): |actual − expected|.
 * Expected ≈ commanded step × pelvis/COM transport fraction (~0.55 of foot step).
 */
export function comProgressMatchErrorM(
  actualComAdvanceM: number,
  commandedForwardM: number,
  transportFraction = 0.55,
): number {
  if (!Number.isFinite(actualComAdvanceM) || !Number.isFinite(commandedForwardM)) {
    return 1;
  }
  const expected = Math.max(0, commandedForwardM) * transportFraction;
  return Math.abs(actualComAdvanceM - expected);
}
